import { ClientsClient, GetClient, GetConversionError } from '../../generated-client/generated-client';
import { Component, OnDestroy, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { Subject, Subscription, of } from 'rxjs';
import { delay, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { ConversionErrorDetailComponent } from '../conversion-error-detail/conversion-error-detail.component';
import { ConversionErrorsService } from '../../services/conversion-errors.service';
import { DataSource } from '@angular/cdk/collections';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MultiSelectClientComponent } from '../multi-select-client/multi-select-client.component';
import { PaginationResult } from 'ng-lightquery';

@Component({
  selector: 'da-conversion-errors',
  templateUrl: './conversion-errors.component.html',
  styleUrls: ['./conversion-errors.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormField,
    MatInput,
    FormsModule,
    MultiSelectClientComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatPaginator,
    DatePipe
  ]
})
export class ConversionErrorsComponent implements OnInit, OnDestroy {
  conversionErrorsService = inject(ConversionErrorsService);
  private dialog = inject(MatDialog);
  private clientsClient = inject(ClientsClient);

  private conversionErrorsPaginatedSubscription: Subscription;
  conversionErrorsPaginated = signal<PaginationResult<GetConversionError>>(null);
  dataSource: DataSource<GetConversionError>;
  private fileNameFilterSource = new Subject<string>();
  fileNameFilter = signal<string>(null);
  clients = signal<GetClient[]>([]);
  selectedClientId: string = null;

  ngOnInit() {
    this.dataSource = this.conversionErrorsService;
    this.fileNameFilter.set(this.conversionErrorsService.getQueryParameter('fileNameFilter'));
    this.fileNameFilterSource
      .pipe(
        switchMap(x => of<string>(x).pipe(delay(250))),
        distinctUntilChanged()
      )
      .subscribe((filterValue: string) => {
        this.conversionErrorsService.setQueryParameter('fileNameFilter', filterValue);
      });
    this.conversionErrorsPaginatedSubscription = this.conversionErrorsService.paginationResult.subscribe(
      r => this.conversionErrorsPaginated.set(r)
    );
    this.clientsClient.getClients(null, null).subscribe(c => this.clients.set(c));
  }

  ngOnDestroy() {
    this.conversionErrorsPaginatedSubscription.unsubscribe();
    this.conversionErrorsService.setQueryParameter('clientId', null);
    this.conversionErrorsService.setQueryParameter('excludedClientId', null);
  }

  onFileNameFilter(fileNameFilter: string) {
    this.fileNameFilter.set(fileNameFilter);
    this.fileNameFilterSource.next(fileNameFilter);
  }

  onPage(pageEvent: PageEvent) {
    this.conversionErrorsService.page = pageEvent.pageIndex + 1;
    this.conversionErrorsService.pageSize = pageEvent.pageSize;
  }

  onSort(event: { active: string; direction: string }) {
    if (!event.direction) {
      this.conversionErrorsService.sort = null;
    } else {
      this.conversionErrorsService.sort = { propertyName: event.active, isDescending: event.direction === 'desc' };
    }
  }

  showDetail(conversionError: GetConversionError) {
    this.dialog.open(ConversionErrorDetailComponent, {
      data: conversionError
    });
  }

  filterClients(clientList: GetClient[]): void {
    this.conversionErrorsService.setQueryParameter('clientId', clientList.length ? clientList.map(item => item.clientId).join() : null);
  }

  excludedClients(clientList: GetClient[]): void {
    this.conversionErrorsService.setQueryParameter(
      'excludedClientId',
      clientList.length ? clientList.map(item => item.clientId).join() : null
    );
  }
}
